import React, { useEffect, useMemo, useState } from "react";
import { ShieldHalf, Hexagon, Award, TrendingUp, Plus, Pencil, Trash2, FileDown } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { PageHeader, MonthYearFilter, SectionCard, Footer } from "@/components/common";
import ExportDialog from "@/components/ExportDialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { exportToPDF } from "@/lib/pdfExport";
import { MONTHS, MONTH_SHORT } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

function MiniCard({ icon: Icon, iconBg, iconColor, label, children, spark, sparkColor = "#2563eb", highlight }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-green-300 bg-green-50/40" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: iconBg }}><Icon className="w-5 h-5" style={{ color: iconColor }} /></div>
        <p className="text-sm text-slate-500 font-medium">{label}</p>
      </div>
      {children}
      {spark && <div className="h-10 mt-2"><ResponsiveContainer><AreaChart data={spark}><Area type="monotone" dataKey="value" stroke={sparkColor} fill={sparkColor} fillOpacity={0.15} strokeWidth={2} /></AreaChart></ResponsiveContainer></div>}
    </div>
  );
}

function InputDialog({ open, onOpenChange, initial, month, year, onSaved }) {
  const [name, setName] = useState("");
  const [bn, setBn] = useState("");
  const [rac, setRac] = useState(0);
  const [apd, setApd] = useState(0);
  useEffect(() => {
    setName(initial?.name || ""); setBn(initial?.bn || "");
    setRac(initial?.rac_actual ?? 0); setApd(initial?.apd_actual ?? 0);
  }, [initial, open]);
  const save = async () => {
    if (!name) { toast.error("Nama wajib diisi"); return; }
    try {
      await api.post("/lif-ccv", { name, bn, old_bn: initial?.bn, month, year, rac_actual: Number(rac), apd_actual: Number(apd) });
      toast.success("Data tersimpan"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error("Gagal menyimpan"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="lc-form">
        <DialogHeader><DialogTitle>{initial ? "Edit" : "Input"} LIF & CCV — {MONTHS[month - 1].l} {year}</DialogTitle>
          <DialogDescription>Target RAC = 3, Target 5S/APD = 1 per bulan (otomatis). Isi nilai aktual.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div><Label className="text-xs">Nama</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="lc-name" /></div>
          <div><Label className="text-xs">BN</Label><Input value={bn} onChange={(e) => setBn(e.target.value)} data-testid="lc-bn" /></div>
          <div><Label className="text-xs">RAC Actual (0-3)</Label><Input type="number" min={0} max={3} value={rac} onChange={(e) => setRac(e.target.value)} data-testid="lc-rac" /></div>
          <div><Label className="text-xs">5S / APD Actual (0-1)</Label><Input type="number" min={0} max={1} value={apd} onChange={(e) => setApd(e.target.value)} data-testid="lc-apd" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="lc-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function konsColor(p) { return p >= 100 ? "#16a34a" : p >= 75 ? "#2563eb" : "#f59e0b"; }

export default function LIFCCV() {
  const { canEdit } = useAuth();
  const _now = new Date();
  const [month, setMonth] = useState(_now.getMonth() + 1);
  const [year, setYear] = useState(_now.getFullYear());
  const [data, setData] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => { const r = await api.get("/lif-ccv/summary", { params: { month, year } }); setData(r.data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, year]);

  if (!data) return <div className="p-6"><div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>;

  const c = data.cards;
  const mLabel = MONTHS[month - 1].l;
  const spark = data.rac_apd.trend.map((t) => ({ value: t.value }));
  const distData = [
    { name: "Excellent (100%)", value: data.distribution.excellent, color: "#2563eb" },
    { name: "Good (75%)", value: data.distribution.good, color: "#ea580c" },
    { name: "Butuh Perhatian (<75%)", value: data.distribution.butuh_perhatian, color: "#f59e0b" },
  ];
  const racApdDonut = [
    { name: "Actual", value: data.rac_apd.total_actual, color: "#2563eb" },
    { name: "Sisa", value: Math.max(data.rac_apd.total_target - data.rac_apd.total_actual, 0), color: "#f59e0b" },
  ];
  const barData = data.employees.map((e) => ({ name: e.name, Akumulasi: e.akumulasi, "Bulan Ini": e.bulan_ini }));

  const del = async () => { const e = delTarget; setDelTarget(null); try { await api.delete(`/lif-ccv/employee`, { params: { bn: e.bn || "", name: e.name || "" } }); toast.success("Data karyawan dihapus"); load(); } catch (err) { toast.error("Gagal"); } };

  return (
    <div className="p-6" id="lifccv-page">
      <PageHeader icon={ShieldHalf} title="HSE MONITORING - LIF & CCV" subtitle={`Overview Pemantauan LIF & CCV di Seluruh Area (Data ${mLabel} ${year})`}
        right={<div className="flex items-center gap-2"><MonthYearFilter month={month} setMonth={setMonth} year={year} setYear={setYear} />
          {canEdit && <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-9" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="lc-add"><Plus className="w-4 h-4 mr-1" />Input</Button>}
          <Button size="sm" variant="outline" className="h-9" onClick={() => setExportOpen(true)} data-testid="lc-export"><FileDown className="w-4 h-4 mr-1" />Export</Button></div>} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MiniCard icon={ShieldHalf} iconBg="#ede9fe" iconColor="#7c3aed" label={`Total LIF & CCV (${mLabel})`} spark={spark} sparkColor="#2563eb">
          <p className="text-3xl font-extrabold">{c.total_actual}</p><p className="text-xs text-slate-400">vs Target: {c.total_target}</p>
        </MiniCard>
        <MiniCard icon={Hexagon} iconBg="#ffedd5" iconColor="#ea580c" label={`Consistency Rata-Rata (${mLabel})`} spark={spark} sparkColor="#f59e0b">
          <p className="text-3xl font-extrabold">{c.consistency_avg}%</p><p className="text-xs text-slate-400">{c.num_employees} Karyawan</p>
        </MiniCard>
        <MiniCard icon={Award} iconBg="#dcfce7" iconColor="#16a34a" label="Top Performer (Konsistensi)" highlight>
          <p className="text-base font-semibold text-slate-700">{c.top?.name || "-"}</p><p className="text-4xl font-extrabold">{c.top?.pct ?? 0}%</p>
        </MiniCard>
        <MiniCard icon={TrendingUp} iconBg="#fee2e2" iconColor="#dc2626" label="Butuh Peningkatan">
          <p className="text-base font-semibold text-slate-700">{c.low?.name || "-"}</p><p className="text-4xl font-extrabold">{c.low?.pct ?? 0}%</p>
        </MiniCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title={`LIF & CCV Performance Summary - ${mLabel} ${year}`}>
          <p className="text-sm font-semibold text-slate-600 mb-2">Distribusi Konsistensi Karyawan</p>
          <div className="flex items-center gap-4">
            <div className="w-32 h-32"><ResponsiveContainer><PieChart><Pie data={distData} dataKey="value" innerRadius={40} outerRadius={60} paddingAngle={2}>{distData.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie></PieChart></ResponsiveContainer></div>
            <div className="space-y-1.5 text-sm flex-1">{distData.map((d) => <div key={d.name} className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />{d.name}<span className="ml-auto font-bold">{d.value}</span></div>)}</div>
          </div>
          <p className="text-sm font-semibold text-slate-600 mt-5 mb-2">Total RAC / 5S / APD ({mLabel})</p>
          <div className="flex items-center gap-3">
            <div className="w-28 h-28 relative"><ResponsiveContainer><PieChart><Pie data={racApdDonut} dataKey="value" innerRadius={34} outerRadius={54}>{racApdDonut.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie></PieChart></ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center"><span className="text-[10px] text-slate-400">Total</span><span className="text-lg font-extrabold">actual: {data.rac_apd.total_actual}</span></div></div>
            <div className="flex-1"><ResponsiveContainer width="100%" height={110}><LineChart data={data.rac_apd.trend}><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} /></LineChart></ResponsiveContainer>
              <p className="text-xs text-slate-400 text-center">Total target: {data.rac_apd.total_target}</p></div>
          </div>
        </SectionCard>

        <SectionCard title={`HSE Monitoring - LIF & CCV Employee Performance Breakdown (${mLabel} ${year})`} className="lg:col-span-2">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="lc-table">
              <thead>
                <tr className="text-slate-500 border-b">
                  <th rowSpan={2} className="px-2 py-2 text-left">No</th>
                  <th rowSpan={2} className="px-2 py-2 text-left">Nama</th>
                  <th rowSpan={2} className="px-2 py-2 text-left">BN</th>
                  <th colSpan={2} className="px-2 py-1 text-center border-l">RAC ({mLabel})</th>
                  <th colSpan={2} className="px-2 py-1 text-center border-l">5S / APD ({mLabel})</th>
                  <th colSpan={3} className="px-2 py-1 text-center border-l">Total</th>
                  <th colSpan={2} className="px-2 py-1 text-center border-l">LIF & CCV Jan - {MONTH_SHORT[month - 1]}</th>
                  {canEdit && <th rowSpan={2} className="px-2 py-2 text-center border-l">Aksi</th>}
                </tr>
                <tr className="text-slate-400 border-b">
                  <th className="px-2 py-1 border-l">Target</th><th className="px-2 py-1">Actual</th>
                  <th className="px-2 py-1 border-l">Target</th><th className="px-2 py-1">Actual</th>
                  <th className="px-2 py-1 border-l">Target</th><th className="px-2 py-1">Actual</th><th className="px-2 py-1">Konsistensi%</th>
                  <th className="px-2 py-1 border-l">Target</th><th className="px-2 py-1">Actual</th>
                </tr>
              </thead>
              <tbody>
                {data.employees.map((e) => (
                  <tr key={e.bn} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-2 py-2">{e.no}</td>
                    <td className="px-2 py-2 font-medium whitespace-nowrap">{e.name}</td>
                    <td className="px-2 py-2">{e.bn}</td>
                    <td className="px-2 py-2 text-center border-l">{e.rac_target}</td><td className="px-2 py-2 text-center font-semibold">{e.rac_actual}</td>
                    <td className="px-2 py-2 text-center border-l">{e.apd_target}</td><td className="px-2 py-2 text-center font-semibold">{e.apd_actual}</td>
                    <td className="px-2 py-2 text-center border-l">{e.total_target}</td><td className="px-2 py-2 text-center font-semibold">{e.total_actual}</td>
                    <td className="px-2 py-2 min-w-[110px]"><div className="text-[10px] font-semibold mb-0.5">{e.konsistensi}%</div><div className="h-2 bg-slate-100 rounded-full"><div className="h-2 rounded-full" style={{ width: `${Math.min(e.konsistensi, 100)}%`, background: konsColor(e.konsistensi) }} /></div></td>
                    <td className="px-2 py-2 text-center border-l">{e.ytd_target}</td><td className="px-2 py-2 text-center font-semibold">{e.ytd_actual}</td>
                    {canEdit && <td className="px-2 py-2 text-center border-l whitespace-nowrap"><div className="flex items-center justify-center gap-1">
                      <button onClick={() => { setEditing(e); setFormOpen(true); }} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" data-testid={`lc-edit-${e.bn}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setDelTarget(e)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`lc-del-${e.bn}`}><Trash2 className="w-3.5 h-3.5" /></button></div></td>}
                  </tr>
                ))}
                {data.employees.length === 0 && <tr><td colSpan={13} className="text-center py-8 text-slate-400">Tidak ada data</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="mt-5">
            <ResponsiveContainer width="100%" height={40 + barData.length * 46}>
              <BarChart data={barData} layout="vertical" margin={{ left: 20, right: 30 }}>
                <XAxis type="number" hide /><YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="Akumulasi" stackId="a" fill="#2563eb" radius={[4, 0, 0, 4]}><LabelInside /></Bar>
                <Bar dataKey="Bulan Ini" stackId="a" fill="#f59e0b" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-6 text-xs mt-1">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-600" /> Akumulasi</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-500" /> Bulan Ini</span>
            </div>
          </div>
        </SectionCard>
      </div>

      <InputDialog open={formOpen} onOpenChange={setFormOpen} initial={editing} month={month} year={year} onSaved={load} />
      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Hapus data LIF & CCV?</AlertDialogTitle><AlertDialogDescription>Seluruh data LIF & CCV untuk karyawan {delTarget?.name} akan dihapus permanen.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={del} data-testid="lc-del-confirm">Hapus</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} onExport={(o) => exportToPDF("lifccv-page", `LIF_CCV_${month}_${year}`, o)} />
      <Footer />
    </div>
  );
}

function LabelInside() { return null; }
